import { apiRequest } from './client';

export type AlignmentStatus = 'similar' | 'partial' | 'different';

export interface RegulationMeta {
  artifact_id: string;
  title: string | null;
  jurisdiction: string | null;
  status: string | null;
  effective_date: string | null;
  // When this regulation's content was last fetched into our DB (data recency).
  fetched_at?: string | null;
}

export interface ComparisonMetadata {
  regulation_1: RegulationMeta;
  regulation_2: RegulationMeta;
  overall_alignment: AlignmentStatus;
  overall_similarity_score: number;
  generated_at: string;
}

export interface DetailedPoint {
  point: string;        // The specific provision or requirement
  explanation: string;  // What this means in plain language
  impact: string;       // What this affects in practice
}

export interface ComparisonSection {
  category: string;
  category_id: string;
  is_standard: boolean;  // true = one of 10 standard dimensions; false = policy-specific
  alignment: AlignmentStatus;
  alignment_label: string;
  key_insight: string;
  section_summary: string;
  comparison_impact: string;
  regulation_1_summary: string;
  regulation_2_summary: string;
  regulation_1_points: DetailedPoint[];
  regulation_2_points: DetailedPoint[];
}

export interface ComparisonResult {
  metadata: ComparisonMetadata;
  executive_summary: string;
  comparison_sections: ComparisonSection[];
  unique_to_regulation_1: string[];
  unique_to_regulation_2: string[];
  common_grounds: string[];
}

export async function compareRegulations(
  artifactIds: [string, string],
  token: string,
): Promise<ComparisonResult> {
  return apiRequest<ComparisonResult>('/api/v1/comparison/compare', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ artifact_ids: artifactIds }),
  });
}

// ---------------------------------------------------------------------------
// Requirement matrix (SRS #169) with gaps & recommendations (SRS #45).
// A side is a regulation artifact UUID or "document:<id>" for one of the
// caller's own uploaded documents (SRS #44).
// ---------------------------------------------------------------------------

export const documentRef = (id: number) => `document:${id}`;

export interface MatrixSide {
  side_index: number;
  artifact_id: string;
  title: string | null;
  jurisdiction: string | null;
  status: string | null;
  kind?: 'regulation' | 'document';
  extracted: boolean;
}

export interface MatrixCell {
  side_index: number;
  coverage: 'covered' | 'partial' | 'absent';
  points: string[];
}

export interface MatrixRow {
  category_id: string;
  category: string;
  is_standard: boolean;
  cells: MatrixCell[];
  sides_covering: number;
  complete: boolean;
}

export interface MatrixGap {
  side_index: number;
  category_id: string;
  severity: 'high' | 'medium' | 'low';
  description: string;
}

export interface MatrixRecommendation {
  priority: number;
  side_index: number | null;
  category_id: string | null;
  action: string;
  rationale?: string;
}

export interface ComparisonMatrix {
  sides: MatrixSide[];
  rows: MatrixRow[];
  completeness: {
    rows: number;
    complete_rows: number;
    ratio: number;
    per_side: Array<{ side_index: number; covered: number; partial: number; absent: number }>;
  };
  executive_summary: string;
  gaps: MatrixGap[];
  recommendations: MatrixRecommendation[];
  analysis_note: string | null;
  generated_at: string;
}

export async function fetchComparisonMatrix(sides: string[], token: string, withAnalysis = true): Promise<ComparisonMatrix> {
  return apiRequest<ComparisonMatrix>('/api/v1/comparison/matrix', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ sides, with_analysis: withAnalysis }),
  });
}
